import { Helmet } from "react-helmet-async";

const SITE_URL = "https://procureaide.co";
const DEFAULT_IMAGE = "https://static.prod-images.emergentagent.com/jobs/eaa678b7-9af1-4aad-8a6a-f3c851cbd4dd/images/dbce5893cc592c9f592f0b62081d20863713d3d2eab96175b7da3cb817adfe63.jpeg";

const SEO_DEFAULTS = {
  siteName: "ProcureAide",
  defaultTitle: "ProcureAide | AI-Powered Procurement Intelligence",
  defaultDescription: "AI-powered procurement intelligence platform. DataRefinery, SpendLens and ConTrack help organisations gain spend visibility, manage contracts and unlock savings across Pakistan and Canada.",
};

export const SEOHead = ({
  title,
  description,
  path = "",
  image,
  type = "website",
  article,
  noindex = false,
  jsonLd,
}) => {
  const fullTitle = title
    ? `${title} | ${SEO_DEFAULTS.siteName}`
    : SEO_DEFAULTS.defaultTitle;
  const desc = description || SEO_DEFAULTS.defaultDescription;
  const url = `${SITE_URL}${path}`;
  const img = image || DEFAULT_IMAGE;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={url} />

      {noindex && <meta name="robots" content="noindex,nofollow" />}

      {/* Geographic targeting */}
      <link rel="alternate" hrefLang="en-PK" href={url} />
      <link rel="alternate" hrefLang="en-CA" href={url} />
      <link rel="alternate" hrefLang="x-default" href={url} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:image" content={img} />
      <meta property="og:site_name" content={SEO_DEFAULTS.siteName} />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={img} />

      {/* Article-specific */}
      {article && <meta property="article:published_time" content={article.publishedAt} />}
      {article && <meta property="article:author" content={article.author} />}
      {article?.tags?.map((tag) => (
        <meta property="article:tag" content={tag} key={tag} />
      ))}

      {/* JSON-LD structured data */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
};

// Pre-built JSON-LD generators
export const buildBreadcrumbLD = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": items.map((item, i) => ({
    "@type": "ListItem",
    "position": i + 1,
    "name": item.name,
    "item": `${SITE_URL}${item.path}`,
  })),
});

export const buildArticleLD = ({ title, description, slug, author, publishedAt, tags, image }) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": title,
  "description": description,
  "url": `${SITE_URL}/insights/${slug}`,
  "image": image || DEFAULT_IMAGE,
  "author": { "@type": "Organization", "name": author || "ProcureAide" },
  "publisher": {
    "@type": "Organization",
    "name": "ProcureAide",
    "logo": { "@type": "ImageObject", "url": DEFAULT_IMAGE },
  },
  "datePublished": publishedAt,
  "keywords": tags?.join(", "),
});

export const buildFAQLD = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map((faq) => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": { "@type": "Answer", "text": faq.answer },
  })),
});

export const buildProductLD = ({ name, description, path }) => ({
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  "name": `ProcureAide ${name}`,
  "description": description,
  "url": `${SITE_URL}${path}`,
  "applicationCategory": "BusinessApplication",
  "operatingSystem": "Web",
  "offers": {
    "@type": "Offer",
    "price": "0",
    "priceCurrency": "USD",
    "description": "Contact for pricing",
  },
  "provider": {
    "@type": "Organization",
    "name": "ProcureAide",
    "url": SITE_URL,
  },
});
